// Iter 136 — "Where to Watch" TV broadcaster panel.
// Fetches /api/matches/:id/broadcasts (TheSportsDB, cached 24h server-side)
// and renders the channels carrying THIS specific fixture worldwide,
// highlighting any that match the viewer's saved country preference.
//
// Strict no-fabrication rule: if the upstream returns 0 listings or
// can't match the fixture, we render an explicit "no listings available"
// notice rather than inventing channels.

import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Card } from "../components/ui/card";
import { Tv, Globe2, MapPin, AlertCircle, Bell, Check } from "lucide-react";
import { API } from "../App";
import { useAuth } from "../context/AuthContext";

const STATUS_COPY = {
  no_listings: {
    title: "No TV listings available",
    body: "This match is recognised in our broadcaster database, but no TV channels are listed for it yet.",
  },
  not_matched: {
    title: "No TV listings available",
    body: "We couldn't find this fixture in the international TV schedule database.",
  },
  key_missing: {
    title: "TV listings unavailable",
    body: "The broadcaster lookup service is not configured on this server.",
  },
};

const ChannelLogo = ({ src, alt }) => {
  const [errored, setErrored] = useState(false);
  if (!src || errored) {
    return (
      <div className="w-8 h-8 rounded bg-white/10 flex items-center justify-center shrink-0">
        <Tv className="w-4 h-4 text-white/40" />
      </div>
    );
  }
  return (
    <img
      src={src}
      alt={alt || ""}
      onError={() => setErrored(true)}
      loading="lazy"
      className="w-8 h-8 rounded object-contain bg-white/5 p-0.5 shrink-0"
    />
  );
};

// Iter 137 — per-channel row with a "Remind me 30m before kickoff" button.
// Shown for ANY channel highlighted as user-country OR home-league-country
// (i.e. the channels the viewer is most likely to actually tune into).
// One click POSTs to /api/me/match-reminders; backend computes fire_at
// = kickoff - 30min and a cron loop dispatches a Web Push.
const ChannelRow = ({ channel: c, rowIndex, matchInfo }) => {
  const { user } = useAuth();
  const [reminding, setReminding] = useState(false);
  const [reminded, setReminded] = useState(false);

  // Kickoff must include time-of-day for a meaningful 30-min lead.
  // `match_info.date` typically already includes time (ISO string).
  const kickoffIso = matchInfo?.date || matchInfo?.kickoff_iso;
  const matchId = matchInfo?.fixture_id || matchInfo?.match_id;
  const league = matchInfo?.league;

  const eligibleForReminder =
    !!user && !!kickoffIso && !!matchId && (c.is_user_country || c.is_home_league_country);

  // Only show reminder if kickoff is at least 30 minutes away.
  const tooLate = useMemo(() => {
    if (!kickoffIso) return true;
    try {
      const koMs = new Date(kickoffIso).getTime();
      return koMs - Date.now() <= 30 * 60 * 1000;
    } catch {
      return true;
    }
  }, [kickoffIso]);

  const handleRemind = async () => {
    if (!user) {
      toast.error("Please log in to set reminders.");
      return;
    }
    setReminding(true);
    try {
      await axios.post(`${API}/me/match-reminders`, {
        match_id: String(matchId),
        home_team: matchInfo?.home_team || "",
        away_team: matchInfo?.away_team || "",
        kickoff_iso: kickoffIso,
        league: league || null,
        channel_name: c.name,
        channel_country: c.country || null,
      });
      setReminded(true);
      toast.success(`Reminder set · ${c.name} · 30 min before kickoff`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not set reminder");
    } finally {
      setReminding(false);
    }
  };

  return (
    <li
      data-testid={`tv-channel-row-${rowIndex}`}
      className={[
        "flex items-center gap-3 rounded-lg px-3 py-2 border transition",
        c.is_user_country
          ? "bg-emerald-500/10 border-emerald-500/40"
          : c.is_home_league_country
          ? "bg-amber-500/10 border-amber-500/30"
          : "bg-white/[0.03] border-white/10 hover:bg-white/[0.06]",
      ].join(" ")}
    >
      <ChannelLogo src={c.logo} alt={c.name} />
      <div className="min-w-0 flex-1">
        <p className="text-white text-sm font-semibold truncate">{c.name}</p>
        <p className="text-[11px] text-gray-400 truncate">
          {c.country || "International"}
        </p>
      </div>
      <div className="flex items-center gap-1.5 shrink-0">
        {c.is_user_country && (
          <span className="text-[9px] uppercase tracking-[0.14em] font-bold text-emerald-300 bg-emerald-500/15 px-1.5 py-0.5 rounded">
            Your country
          </span>
        )}
        {!c.is_user_country && c.is_home_league_country && (
          <span
            className="text-[9px] uppercase tracking-[0.14em] font-bold text-amber-300 bg-amber-500/15 px-1.5 py-0.5 rounded"
            title="Channel from the league's home country"
          >
            League country
          </span>
        )}
        {eligibleForReminder && !tooLate && (
          <button
            type="button"
            onClick={handleRemind}
            disabled={reminding || reminded}
            data-testid={`tv-remind-btn-${rowIndex}`}
            title={reminded ? "Reminder set" : "Remind me 30 min before kickoff"}
            className={[
              "inline-flex items-center justify-center w-7 h-7 rounded-full border transition",
              reminded
                ? "bg-emerald-500/20 border-emerald-500/40 text-emerald-300"
                : "bg-white/[0.04] border-white/10 text-white/70 hover:text-white hover:bg-white/[0.08]",
              reminding ? "animate-pulse" : "",
            ].join(" ")}
          >
            {reminded ? <Check className="w-3.5 h-3.5" /> : <Bell className="w-3.5 h-3.5" />}
          </button>
        )}
      </div>
    </li>
  );
};

export const WhereToWatchSection = ({ matchInfo }) => {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [showAll, setShowAll] = useState(false);

  const matchId = matchInfo?.fixture_id || matchInfo?.match_id;
  const home = matchInfo?.home_team;
  const away = matchInfo?.away_team;
  // The analysis modal exposes `match_info.date` as ISO date or full ISO.
  // We need just the kickoff date in YYYY-MM-DD (UTC).
  const dateIso = useMemo(() => {
    const raw = matchInfo?.date || matchInfo?.kickoff_iso;
    if (!raw) return null;
    try {
      const d = new Date(raw);
      // Convert to UTC YYYY-MM-DD because TheSportsDB stores UTC kickoff dates.
      return new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate()))
        .toISOString()
        .slice(0, 10);
    } catch {
      return null;
    }
  }, [matchInfo?.date, matchInfo?.kickoff_iso]);

  useEffect(() => {
    if (!matchId || !home || !away || !dateIso) return;
    let cancelled = false;
    setLoading(true);
    setError(null);
    const params = new URLSearchParams({ home, away, date: dateIso });
    if (user?.tv_country) params.set("country", user.tv_country);
    axios
      .get(`${API}/matches/${encodeURIComponent(matchId)}/broadcasts?${params.toString()}`)
      .then((r) => {
        if (!cancelled) setData(r.data);
      })
      .catch((err) => {
        if (!cancelled) setError(err?.response?.data?.detail || "Could not load TV listings");
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [matchId, home, away, dateIso, user?.tv_country]);

  // Sort: user-country first, then alphabetic by country, then channel name.
  // (Hook called unconditionally so Rules-of-Hooks holds regardless of
  // whether the section ends up rendering for this match.)
  const channels = useMemo(() => {
    if (!data?.channels) return [];
    const arr = [...data.channels];
    // Sort priority: user-country → home-league-country → alphabetic.
    arr.sort((a, b) => {
      if (a.is_user_country !== b.is_user_country) return a.is_user_country ? -1 : 1;
      if (a.is_home_league_country !== b.is_home_league_country)
        return a.is_home_league_country ? -1 : 1;
      const ac = (a.country || "").toLowerCase();
      const bc = (b.country || "").toLowerCase();
      if (ac !== bc) return ac.localeCompare(bc);
      return (a.name || "").localeCompare(b.name || "");
    });
    return arr;
  }, [data]);

  if (!matchId || !home || !away || !dateIso) return null;

  const userCountryHits = channels.filter((c) => c.is_user_country);
  const showStatus = data?.status && data.status !== "ok" ? STATUS_COPY[data.status] : null;
  const PREVIEW_LIMIT = 10;
  const visible = showAll ? channels : channels.slice(0, PREVIEW_LIMIT);

  return (
    <Card
      className="bg-gradient-to-br from-indigo-900/30 to-cyan-900/20 border-indigo-500/30 p-6"
      data-testid="where-to-watch-section"
    >
      <div className="flex items-center gap-2 mb-4">
        <Tv className="w-6 h-6 text-cyan-400" />
        <h3 className="text-2xl font-bold text-white">Where to Watch</h3>
        {data?.cached && (
          <span
            className="ml-auto text-[10px] uppercase tracking-[0.12em] text-cyan-300/70 font-mono"
            title="Served from 24h cache"
          >
            cached
          </span>
        )}
      </div>

      {loading && (
        <div className="text-sm text-cyan-200/70" data-testid="where-to-watch-loading">
          Looking up TV channels…
        </div>
      )}

      {error && !loading && (
        <div className="flex items-start gap-2 text-amber-300/90 text-sm">
          <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" />
          <span>{error}</span>
        </div>
      )}

      {!loading && !error && showStatus && (
        <div
          className="flex items-start gap-3 rounded-lg bg-white/[0.03] border border-white/10 p-4"
          data-testid="where-to-watch-empty"
        >
          <AlertCircle className="w-5 h-5 text-amber-400 mt-0.5 shrink-0" />
          <div>
            <p className="text-white font-semibold">{showStatus.title}</p>
            <p className="text-gray-400 text-sm mt-1">{showStatus.body}</p>
          </div>
        </div>
      )}

      {!loading && !error && data?.status === "ok" && (
        <>
          {user?.tv_country && userCountryHits.length === 0 && (
            <div
              className="flex items-start gap-2 mb-4 rounded-lg bg-amber-500/10 border border-amber-500/30 p-3 text-sm text-amber-200"
              data-testid="where-to-watch-no-user-country"
            >
              <MapPin className="w-4 h-4 mt-0.5 shrink-0" />
              <span>
                No broadcaster listed for your country ({user.tv_country}). International coverage below.
              </span>
            </div>
          )}

          {!user?.tv_country && (
            <div
              className="flex items-start gap-2 mb-4 rounded-lg bg-blue-500/10 border border-blue-500/20 p-3 text-xs text-blue-200/80"
              data-testid="where-to-watch-set-country-cta"
            >
              <Globe2 className="w-3.5 h-3.5 mt-0.5 shrink-0" />
              <span>
                Tip: set your country in <strong>My Account</strong> to highlight broadcasters available where you live.
              </span>
            </div>
          )}

          <ul className="grid grid-cols-1 sm:grid-cols-2 gap-2" data-testid="where-to-watch-list">
            {visible.map((c, i) => (
              <ChannelRow
                key={`${c.name}-${c.country}-${i}`}
                channel={c}
                rowIndex={i}
                matchInfo={matchInfo}
              />
            ))}
          </ul>

          {channels.length > PREVIEW_LIMIT && (
            <button
              type="button"
              onClick={() => setShowAll((v) => !v)}
              data-testid="where-to-watch-toggle"
              className="mt-3 text-xs text-cyan-300 hover:text-cyan-200 underline underline-offset-4"
            >
              {showAll
                ? "Show fewer broadcasters"
                : `Show all ${channels.length} broadcasters`}
            </button>
          )}

          <p className="mt-4 text-[10px] text-gray-500 uppercase tracking-[0.14em]">
            TV data via TheSportsDB · cached 24h
          </p>
        </>
      )}
    </Card>
  );
};

export default WhereToWatchSection;
